import React from "react";

import { INIFINITY, NEGATIVE_INFINITY } from "./helpers.cio.drawerContent";

type FilterPriceRangeInputProps = {
  minOnChange: (e: React.FocusEvent<HTMLInputElement>) => void;
  maxOnChange: (e: React.FocusEvent<HTMLInputElement>) => void;
  priceRange?: { min?: string; max?: string };
  "data-testid"?: string;
};

export const FilterPriceRangeInput: React.FC<FilterPriceRangeInputProps> = ({
  minOnChange,
  maxOnChange,
  priceRange,
}) => {
  const [min, setMin] = React.useState(priceRange?.min ?? NEGATIVE_INFINITY);
  const [max, setMax] = React.useState(priceRange?.max ?? INIFINITY);

  React.useEffect(() => {
    setMin(priceRange?.min ?? NEGATIVE_INFINITY);
    setMax(priceRange?.max ?? INIFINITY);
  }, [priceRange?.min, priceRange?.max]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "Home",
      "End",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
    ];

    if (
      allowedKeys.includes(e.key) ||
      (e.ctrlKey && ["a", "c", "v", "x"].includes(e.key.toLowerCase()))
    ) {
      return;
    }

    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  // Positive integers only (SFCC-11838). `onKeyDown` already blocks typed
  // non-digits, but paste (Ctrl+V is allowlisted) and the native number-input
  // ArrowUp/ArrowDown stepper deliver their value straight to `onChange` —
  // "-1", "-5", "5.5", "1e3" all arrived here unfiltered. Rejecting the change
  // keeps the previous committed value (React restores the controlled input).
  const isCommittable = (value: string) =>
    value.length <= 10 && /^\d*$/.test(value);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isCommittable(e.target.value)) setMin(e.target.value);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isCommittable(e.target.value)) setMax(e.target.value);
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
    e.preventDefault();
  };

  return (
    <div className="grid max-w-96 grid-cols-[1fr_auto_1fr] grid-rows-[auto_auto] gap-2">
      {/* Min Input */}
      <label
        className="row-start-1 gap-2 font-grotesque text-sm text-primary"
        htmlFor="min-price-input"
      >
        <div>Min</div>
      </label>
      <div className="row-start-2 flex items-center rounded-xl border border-primary px-2 py-1 outline-accent focus-within:outline-2 sm:px-4 sm:py-3">
        <span className="mr-2 font-grotesque text-lg text-primary">$</span>
        <input
          id="min-price-input"
          type="number"
          min={0}
          step={1}
          className="w-full font-grotesque text-lg text-primary outline-none"
          value={min}
          onChange={handleMinChange}
          onBlur={e => minOnChange(e)}
          onKeyDown={handleKeyDown}
          onWheel={handleWheel}
        />
      </div>

      <span className="row-start-2 self-center font-grotesque text-primary">
        to
      </span>

      {/* Max Input */}
      <label
        className="col-start-3 row-start-1 gap-2 font-grotesque text-sm text-primary"
        htmlFor="max-price-input"
      >
        <div>Max</div>
      </label>
      <div className="row-start-2 flex items-center rounded-xl border border-primary px-2 py-1 outline-primary-hover focus-within:outline-2 sm:px-4 sm:py-3">
        <span className="mr-2 font-grotesque text-lg text-primary">$</span>
        <input
          id="max-price-input"
          type="number"
          min={0}
          step={1}
          className="w-full font-grotesque text-lg text-primary outline-none"
          value={max}
          onChange={handleMaxChange}
          onBlur={e => maxOnChange(e)}
          onKeyDown={handleKeyDown}
          onWheel={handleWheel}
        />
      </div>
    </div>
  );
};
